import { dragonBones } from 'dragonbones-pixi';
import Interactable from '../../../Component/Interactable';
import Renderable from '../../../Component/Renderable';
import Game from '../../../Game';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import Container from '../../../Prototype/Container';
import HelperFriend from '../../../Prototype/Level/HelperFriend';
import { IGameState } from '../../../State/Game/IGameState';
import { HelperFriendActions } from '../../../State/Level/HelperFriend/HelperFriendActions';
import { HelperFriendStatus } from '../../../State/Level/HelperFriend/HelperFriendStatus';
import { LevelStatus } from '../../../State/Level/ILevelState';
import { ResourceDriver } from '../../../Utility/Resource/ResourceManager';
import {
    StartAnimation,
    StartAnimationAsync,
} from '../../Animation/AnimationHelper';
import {
    AUDIO_UI_BUTTON_DEFAULT,
    AUDIO_WILDCARD_PLUS_THREE_DECK,
} from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { StartAudio } from '../../Audio/AudioHelper';
import BaseUIHandler from '../BaseUIHandler';
import BaseUISystem from '../BaseUISystem';
import LeaderboardHelper from '../LeaderboardHelper';
import { applyDefaultButtonAnimation } from '../UIHelper';
import EventObject = dragonBones.EventObject;

/* prettier-ignore */ const HELPER_FRIEND_GROUP = 'helper_friend_group';
/* prettier-ignore */ const HELPER_FRIEND_CONTAINER = HELPER_FRIEND_GROUP + '/helper_friend_container';
/* prettier-ignore */ const HELPER_FRIEND_BUTTON = HELPER_FRIEND_CONTAINER + '/accept_button';

export default class HelperFriendHUDHandler extends BaseUIHandler {
    private enabled: boolean = true;
    private btnAcceptHelp: GameObject;
    private helperFriendAnim: HelperFriend;

    /**
     * The current status of the helper-friend component so as not to re-trigger the animations
     * in `handleStateChanged()`.
     * TODO: Refactor so the handler does not need to keep track of the components status.
     */
    private helperFriendStatus: HelperFriendStatus;

    constructor(uiSystem: BaseUISystem) {
        super('helperFriendHUD', uiSystem);

        this.alwaysVisible = true;

        this.resources.set(
            'ExtraCardsFromFriends_skeleton_data',
            'resource/dragonbones/helperfriend/skeleton.json'
        );
        this.resources.set(
            'ExtraCardsFromFriends_texture',
            'resource/dragonbones/helperfriend/texture.png'
        );
        this.resources.set(
            'ExtraCardsFromFriends_texture_data',
            'resource/dragonbones/helperfriend/texture.json'
        );
    }

    public async preload(): Promise<void> {
        await super.preload();

        // // Load sounds in the backgrounds.
        Game.resource.add(
            'helper_friend_appear',
            'resource/audio/export/helperfriend/helper_friend_appear.mp3',
            ResourceDriver.SECONDARY
        );
        Game.resource.add(
            'helper_friend_dismiss',
            'resource/audio/export/helperfriend/helper_friend_dismiss.mp3',
            ResourceDriver.SECONDARY
        );
        Game.resource.load(ResourceDriver.SECONDARY);
    }

    public handleStateChanged(state: IGameState) {
        if (
            state.levelState.status !== LevelStatus.INTRO &&
            state.levelState.status !== LevelStatus.ACTIVE &&
            state.levelState.status !== LevelStatus.COMPLETE
        ) {
            return;
        }

        // Check if helperFriendStatus has changed.
        if (this.helperFriendStatus !== state.levelState.helperFriendStatus) {
            this.helperFriendStatus = state.levelState.helperFriendStatus;
            switch (this.helperFriendStatus) {
                case HelperFriendStatus.ENABLED:
                case HelperFriendStatus.DISABLED:
                    this.enableInteractables(false);
                    this.dismissHelperFriend();
                    break;
                case HelperFriendStatus.SHOW:
                    this.showHelperFriend();
                    this.enableInteractables(
                        state.levelState.status === LevelStatus.ACTIVE &&
                        this.enabled
                    );
                    break;
                case HelperFriendStatus.USED:
                    this.enableInteractables(false);
                    this.useHelperFriend();
                    break;
            }
        }
    }

    protected onCreate() {
        this.system.scene.on('ToggleLevelHUD', (toggle: boolean) => {
            this.enabled = toggle;

            // TODO: Line may not be necessary.
            this.handleStateChanged(Game.state.getState());
        });

        this.createHelperFriend();
    }

    protected enableInteractables(value: boolean): void {
        if (!this.helperFriendAnim) return;

        this.btnAcceptHelp.getComponent(Interactable).enabled = value;
    }

    private useHelperFriend() {
        if (!this.helperFriendAnim) return;

        this.helperFriendAnim.getComponent(Renderable).visible = true;

        this.triggerAddCards();

        this.hideHelperFriendButton();

        StartAnimationAsync(this.helperFriendAnim, 'DragonbonesDefault', {
            animationName: 'click',
        });
    }

    private async triggerAddCards() {
        await Game.ticker.setTimeoutPromise(550);

        StartAudio(
            this.system.scene,
            AUDIO_WILDCARD_PLUS_THREE_DECK,
            AudioTrackType.SFX
        );

        await Game.ticker.setTimeoutPromise(200);

        StartAudio(
            this.system.scene,
            AUDIO_WILDCARD_PLUS_THREE_DECK,
            AudioTrackType.SFX
        );

        await Game.ticker.setTimeoutPromise(200);

        StartAudio(
            this.system.scene,
            AUDIO_WILDCARD_PLUS_THREE_DECK,
            AudioTrackType.SFX
        );
    }

    private dismissHelperFriend() {
        if (!this.helperFriendAnim) return;

        this.hideHelperFriendButton();

        StartAudio(
            this.system.scene,
            'helper_friend_dismiss',
            AudioTrackType.SFX
        );

        StartAnimationAsync(this.helperFriendAnim, 'DragonbonesDefault', {
            animationName: 'dismiss',
        }).then(() => {
            this.setHelperFriendVisible(false);
        });
    }

    private setHelperFriendVisible(visible: boolean) {
        this.helperFriendAnim.getComponent(Renderable).visible = visible;
    }

    private setHelperFriendButtonVisible(visible: boolean) {
        this.btnAcceptHelp.getComponent(Renderable).alpha = visible ? 1 : 0;
    }

    private showHelperFriendButton() {
        this.setHelperFriendButtonVisible(false);
        StartAnimation(this.btnAcceptHelp, 'Alpha', {
            startAlpha: 0,
            endAlpha: 1,
            duration: 300,
        });
    }

    private hideHelperFriendButton() {
        this.setHelperFriendButtonVisible(true);
        StartAnimation(this.btnAcceptHelp, 'Alpha', {
            startAlpha: 1,
            endAlpha: 0,
            duration: 300,
        });
    }

    private showHelperFriend() {
        if (!this.helperFriendAnim) return;

        this.helperFriendAnim.getComponent(Renderable).visible = true;

        StartAudio(
            this.system.scene,
            'helper_friend_appear',
            AudioTrackType.SFX
        );

        this.setHelperFriendVisible(true);

        this.showHelperFriendButton();

        StartAnimationAsync(this.helperFriendAnim, 'DragonbonesDefault', {
            animationName: 'intro',
        }).then(() => {
            StartAnimation(this.helperFriendAnim, 'DragonbonesDefault', {
                animationName: 'idle',
                loop: true,
            });
        });
    }

    private async createHelperFriend() {
        // Check if helper-friend component instance already exists.
        if (this.helperFriendAnim !== undefined) return;

        // TODO: Move 'platform friends' to state for consistency and testability.
        const friend = await LeaderboardHelper.getFriendForHelp();

        // If no friends available then hide this component.
        if (!friend) {
            this.enabled = false;

            const container = this.getContainer();
            if (container) {
                container.getComponent(Renderable).visible = false;
            }
            
            return;
        }

        this.element.find(HELPER_FRIEND_GROUP).container.visible = true;

        this.btnAcceptHelp = this.element.find(HELPER_FRIEND_BUTTON);

        this.helperFriendAnim = GameObject.Instantiate(HelperFriend);
        this.helperFriendAnim.addComponent(Interactable, { enabled: true });

        const avatarResourceId = LeaderboardHelper.getPlayerAvatarId(
            friend.user.playerId,
            false
        );

        Game.resource.add(
            avatarResourceId,
            friend.user.avatarUrl,
            ResourceDriver.TERTIARY
        );
        Game.resource
            .wait(avatarResourceId)
            .then(() => {
                if (!this.helperFriendAnim || this.helperFriendAnim.destroyed)
                    return;

                const container = this.helperFriendAnim.container;
                const display = container.armature.getSlot('AVATAR').display;
                const texture = PIXI.Sprite.from(avatarResourceId);
                texture.width = texture.height = 75;
                display.addChild(texture);
                display.texture = null;
            })
            .catch(() => {
                // Avatar failed to load - do nothing.
            });

        const helperFriendContainer = this.getContainer();

        if (!helperFriendContainer) return;

        helperFriendContainer.addChild(this.helperFriendAnim);
        this.helperFriendAnim.container.position.set(-490, -130);

        this.onSelected = this.onSelected.bind(this, friend.user.playerId);
        this.helperFriendAnim.on(GameObjectEvent.SELECTED, this.onSelected);
        this.btnAcceptHelp.on(GameObjectEvent.SELECTED, this.onSelected);

        applyDefaultButtonAnimation(
            this.btnAcceptHelp,
            AUDIO_UI_BUTTON_DEFAULT
        );

        this.setHelperFriendVisible(false);
        this.setHelperFriendButtonVisible(false);

        // Call handleStateChanged again due to async loading of helper-friend.
        this.handleStateChanged(Game.state.getState());
    }

    private onSelected(friendId: string) {
        this.helperFriendAnim.off(EventObject.LOOP_COMPLETE);
        Game.state.dispatch(HelperFriendActions.helperFriendRequest(friendId));
    }

    private getContainer(): Container {
        const helperFriendContainers = GameObject.findByName(
            'helperFriendContainer'
        );

        if (helperFriendContainers && helperFriendContainers.length > 0)
            return helperFriendContainers[0] as Container;

        // Add as a child to 'stackContainer' in card-system for relative positioning.
        const stackOuterContainers = GameObject.findByName(
            'stackOuterContainer'
        );
        if (!stackOuterContainers || stackOuterContainers.length === 0) return;

        const stackContainer: Container = stackOuterContainers[0] as Container;
        if (!stackContainer) return;

        const helperFriendContainer = GameObject.Instantiate(Container, {
            name: 'helperFriendContainer',
        });

        stackContainer.addChild(helperFriendContainer);

        // Send helper-friends to back of `stackOuterContainer`
        stackContainer.container.addChildAt(helperFriendContainer.container, 0);

        return helperFriendContainer;
    }
}
